import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineMinus, AiOutlinePlus } from "react-icons/ai";

import type { Endpoint } from "sandbox/types";

import { useThemeMode } from "sandbox/contexts/ThemeModeContext";
import { MethodBadge, SearchBar } from "sandbox/features/shared";
import { AMP_UNMASK_PROPS } from "shell/api/amplitudePrivacy";

import s from "./EndpointList.module.css";

interface GroupShape {
  endpoints: Endpoint[];
  id: string;
  label: string;
}

interface EndpointListProps {
  groups: GroupShape[];
  onSelect: (endpoint: Endpoint) => void;
  selected?: Endpoint;
}

export const EndpointList = ({
  groups,
  selected,
  onSelect,
}: EndpointListProps) => {
  const { t } = useTranslation(["sandbox", "common"]);
  const [query, setQuery] = useState("");
  const { mode } = useThemeMode();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(
    Object.fromEntries(groups.map(g => [g.id, false])),
  );

  const q = query.toLowerCase().trim();
  const filtered = !q
    ? groups
    : groups
        .map(group => ({
          ...group,
          endpoints: group.endpoints.filter(
            e =>
              e.label.toLowerCase().includes(q) ||
              e.path.toLowerCase().includes(q) ||
              e.method.toLowerCase().includes(q),
          ),
        }))
        .filter(g => g.endpoints.length > 0);

  return (
    <div className={s.root}>
      <div className={s.searchWrap}>
        <SearchBar value={query} onChange={setQuery} />
      </div>

      <div className={s.list}>
        {filtered.map(group => {
          const isOpen = openGroups[group.id] ?? true;
          return (
            <div key={group.id} className={s.group} {...AMP_UNMASK_PROPS}>
              <button
                type="button"
                className={s.groupHeader}
                onClick={() =>
                  setOpenGroups(prev => ({ ...prev, [group.id]: !isOpen }))
                }
                data-testid={group.id}
              >
                <span className={s.groupLabel}>{group.label}</span>
                {isOpen ? <AiOutlineMinus /> : <AiOutlinePlus />}
              </button>
              {isOpen && (
                <div className={s.endpoints}>
                  {group.endpoints.map((endpoint, index) => {
                    const active =
                      endpoint.id === selected?.id &&
                      endpoint.label === selected?.label;
                    return (
                      <button
                        key={endpoint.id + index}
                        type="button"
                        onClick={() => onSelect(endpoint)}
                        className={clsx(
                          s.endpoint,
                          active &&
                            (mode === "dark"
                              ? s.endpointActiveDark
                              : s.endpointActive),
                        )}
                        data-testid={endpoint.id + index}
                      >
                        <span className={s.endpointLabel}>
                          {endpoint.label}
                        </span>
                        <MethodBadge method={endpoint.method} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className={s.empty}>
            {t("components.endpointList.noEndpointsMatch", { query })}
          </p>
        )}
      </div>
    </div>
  );
};
